import './tailwind.css';
import '../@/styles/common.css';
import '../@/styles/prism.css';

import React from 'react';
import HyperDX from '@hyperdx/browser';
import config from 'config';
import App, { AppContext } from 'next/app';
import dynamic from 'next/dynamic';
import { Castoro, Caveat, Inter, Mulish, Poppins, Roboto_Mono } from 'next/font/google';
import { withUrqlClient } from 'next-urql';

import ErrorReporter from '@lyearn/core/utils/ErrorReporter';
import { jsonParse, jsonStringify } from '@lyearn/core/utils/json';
import { withHTTPProtocol } from '@lyearn/core/utils/network/url';
import type { AppBodyProps, AppPropsWithLayout } from '@/components/AppBody';
import { fetchFeatureFlags } from '@/helper/FeatureFlags/utils/fetchFeatureFlags';
import { request } from '@/helper/request';
import setSecurityHeader from '@/helper/setSecurityHeader';
import { getExchanges } from '@/helper/urqlClient';
import registerPolyfills from '@/helper/web-polyfills';
import { AppConfig } from '@/hooks/useAppConfig';
import isomorphicAppConfig from '@/hooks/useAppConfig/isomorphicAppConfig';
import { fetchUser } from '@/modules/auth/hooks/useFetchUser';
import { UserProfile } from '@/modules/auth/types';
import BlogMetadata from '@/modules/blogs/BlogMetadata';
import { FeatureFlagType } from '@/types/schema';

registerPolyfills();

// eslint-disable-next-line no-process-env
if (typeof window !== 'undefined' && process.env.NEXT_PUBLIC_HYPERDX_ENABLED === 'true') {
  HyperDX.init({
    // eslint-disable-next-line no-process-env
    apiKey: process.env.NEXT_PUBLIC_HYPERDX_API_KEY ?? '',
    service: 'lyearn-frontend',
    url: 'https://clickstack.lyearn.com/api',
    consoleCapture: true,
    advancedNetworkCapture: true,
  });
}

const inter = Inter({
  weight: ['400', '500', '600', '700'],
  display: 'swap',
  subsets: ['latin'],
});

const poppins = Poppins({
  weight: ['400', '500', '600', '700'],
  display: 'swap',
  subsets: ['latin'],
});

const nanumPenScript = Caveat({
  weight: ['400'],
  display: 'swap',
  subsets: ['latin'],
  preload: false,
});

const robotoMono = Roboto_Mono({
  display: 'swap',
  subsets: ['latin'],
  preload: false,
});

/**
 * Used only in SomosBelcorp (dynamically from themeConfig), not in @lyearn/ui or frontend-universe
 * So dont delete if you dont find any references
 */
const mulish = Mulish({
  display: 'swap',
  preload: false,
});

/**
 * Used only in COURSE, not in @lyearn/ui
 */
const castoro = Castoro({
  weight: ['400'],
  display: 'swap',
  subsets: ['latin'],
  preload: false,
});

export const FONTS: Record<string, typeof inter> = {
  '--font-inter': inter,
  '--font-poppins': poppins,
  '--font-pen': nanumPenScript,
  '--font-mulish': mulish,
  '--font-castoro': castoro,
  '--font-code': robotoMono,
};

const AppBody: React.ComponentType<AppBodyProps> =
  // eslint-disable-next-line no-process-env
  process.env.NODE_ENV === 'development'
    ? dynamic<AppBodyProps>(
        () =>
          __IS_BROWSER__
            ? import('@/components/AppBody')
            : Promise.resolve({
                default: (() => null) as unknown as React.ComponentType<AppBodyProps>,
              }),
        { ssr: false },
      )
    : (require('@/components/AppBody').default as React.ComponentType<AppBodyProps>);

function MyApp(props: AppPropsWithLayout) {
  if (!__IS_BROWSER__) {
    // Blog og/twitter tags are the only markup any page server-renders (SEO).
    const { initialBlogContent } = props.pageProps;
    return initialBlogContent ? <BlogMetadata initialBlogContent={initialBlogContent} /> : null;
  }

  return <AppBody {...props} fonts={FONTS} />;
}

function logPromise<T>(promiseFn: Promise<T>, label: string, results: string[]): Promise<T> {
  if (__IS_BROWSER__) {
    return promiseFn;
  }

  const before = performance.now();

  return promiseFn.finally(() => {
    const after = performance.now();
    results.push(`${label};dur=${after - before}`);
  });
}

function sendResponse(appContext: AppContext, statusCode: number, redirectToLandingPage?: boolean) {
  const res = appContext.ctx.res;

  if (redirectToLandingPage && res) {
    res.writeHead(302, { Location: config.landingPage });
    res.end();
    return {};
  }

  if (appContext.ctx.res) {
    appContext.ctx.res.statusCode = statusCode;
    appContext.ctx.res.end();
  }
  return { pageProps: {} };
}

MyApp.getInitialProps = async (appContext: AppContext) => {
  const serverHost = appContext.ctx.req?.headers.host;
  const clientHost = config.host_domain;
  const host = serverHost || clientHost;
  const serverTimings: string[] = [];

  // @ts-ignore
  const cookies = appContext.ctx.req?.cookies as Record<string, string> | undefined;

  // early return for sourcemap files. They are not needed on the server
  if (appContext.ctx.res && appContext.router.asPath.endsWith('.map')) {
    return sendResponse(appContext, 404);
  }

  if (!__IS_BROWSER__ && !serverHost) {
    ErrorReporter.error(new Error('could not find host on server'), (event) => {
      event.setExtra('headers', jsonStringify(appContext.ctx.req?.headers).data);

      return event;
    });
  }

  const { data: uiConfig } = jsonParse<AppConfig['ui'] | undefined>(
    appContext.router.query.uiConfig as string,
  );

  console.debug(
    'getInitialProps pathname: %s, url: %s',
    appContext.ctx.pathname,
    appContext.ctx.req?.url,
  );

  const hasUserCookies = cookies?._uid && cookies?.accessToken;

  // fetch blog content if on a blog page
  const isBlogPage = appContext.ctx.pathname.startsWith('/blogs/');
  let blogContent = null;

  try {
    const [appConfig, appProps, user, featureFlags] = await Promise.all([
      logPromise(
        isomorphicAppConfig(host!, uiConfig, !!appContext.router.query.isLoggingEnabled),
        'fetchAppConfig',
        serverTimings,
      ),
      logPromise(App.getInitialProps(appContext), 'getInitialProps', serverTimings),
      logPromise<void | UserProfile>(
        __IS_BROWSER__
          ? Promise.resolve()
          : hasUserCookies
          ? // @ts-ignore
            fetchUser(appContext.ctx.urqlClient, cookies._uid).catch(() => {
              /* on server refreshToken is not there so swallowing that error */
            })
          : Promise.resolve(),
        hasUserCookies ? 'fetchUser' : 'resolveStubUser',
        serverTimings,
      ),
      logPromise<void | FeatureFlagType[]>(
        __IS_BROWSER__
          ? Promise.resolve()
          : hasUserCookies
          ? // @ts-ignore
            fetchFeatureFlags(appContext.ctx.urqlClient)
          : Promise.resolve(),
        hasUserCookies ? 'fetchFeatureFlags' : 'resolveStubFeatureFlags',
        serverTimings,
      ),
    ]);

    if (isBlogPage) {
      const slug = appContext.ctx.query.slug as string;
      const publicId = slug?.split('-').pop() || '';

      try {
        const url = `${config.http_protocol}://${host}/api/blog/content?publicId=${publicId}`;

        const accessToken = cookies?.accessToken;
        const fetchOptions =
          __IS_BROWSER__ || !accessToken
            ? {}
            : {
                headers: {
                  accessToken,
                  Accept: '*/*',
                  'Accept-Encoding': 'gzip, deflate, br',
                  Connection: 'keep-alive',
                },
              };

        const blogResponse = await logPromise(
          request(url, {
            method: 'GET',
            ...fetchOptions,
          }).then((res) => {
            return res.json();
          }),
          'fetchBlogContent',
          serverTimings,
        );

        if (blogResponse.success) {
          blogContent = blogResponse.content;
        }
      } catch (error) {
        console.error('Error fetching blog content:', error);
      }
    }

    const response = appContext.ctx.res;
    if (response && !response.headersSent) {
      setSecurityHeader(response, appConfig);
      response.setHeader('Server-Timing', serverTimings.join(', '));
    }

    return {
      ...appProps,
      pageProps: {
        ...appProps.pageProps,
        appConfig,
        user,
        featureFlags,
        initialBlogContent: blogContent,
      },
    };
  } catch (err: unknown) {
    if (err instanceof Error && err.message === 'ACADEMY_NOT_FOUND') {
      return sendResponse(appContext, 404, true);
    }
    return sendResponse(appContext, 500);
  }
};

export default withUrqlClient((ssrExchange, ctx) => {
  return {
    url: `${withHTTPProtocol(ctx?.req?.headers.host || config.host_domain)}/api/graphql`,
    fetchOptions: () => {
      // @ts-ignore
      const cookies = (ctx?.req?.cookies as Record<string, string>) || undefined;
      const accessToken: string | undefined = cookies?.accessToken;

      if (__IS_BROWSER__ || !accessToken) {
        return {};
      }

      return { headers: { accessToken } };
    },
    exchanges: getExchanges(ssrExchange, false),
  };
  // @ts-ignore
})(MyApp);
